// Константы и вспомогательные компоненты модуля «Проекты и задачи»
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './Tasks.css';

export const TASK_STATUSES = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

export const TASK_PRIORITIES = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'critical', label: 'Критический' },
];

export const PROJECT_STATUSES = [
  { value: 'planned', label: 'Запланирован' },
  { value: 'active', label: 'Активен' },
  { value: 'on_hold', label: 'Приостановлен' },
  { value: 'done', label: 'Завершён' },
  { value: 'cancelled', label: 'Отменён' },
];

export const statusLabel = (v, list = TASK_STATUSES) =>
  (list.find((s) => s.value === v) || {}).label || v;

export const StatusPill = ({ value, list = TASK_STATUSES }) => (
  <span className={`tasks-status-pill ${value}`}>{statusLabel(value, list)}</span>
);

export const PriorityPill = ({ value }) => (
  <span className={`tasks-priority-pill ${value}`}>{statusLabel(value, TASK_PRIORITIES)}</span>
);

export const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('ru-RU');
  } catch {
    return String(d);
  }
};

export const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleString('ru-RU');
  } catch {
    return String(d);
  }
};

// ────────── Модалка задачи ──────────

export function TaskModal({ taskId, projectId, onClose, onSaved, projects = [], users = [], parentId = null, initialIsMilestone = false }) {
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [timeLogs, setTimeLogs] = useState([]);
  const [timeForm, setTimeForm] = useState({ hours: '', description: '', logged_date: '' });
  const [savingTime, setSavingTime] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const [depForm, setDepForm] = useState({ from_task_id: '', lag_days: 0 });
  const [tab, setTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', assignee: '', priority: 'medium',
    due_date: '', status: 'new', project: projectId || '',
    is_milestone: !!initialIsMilestone, duration_days: '', progress_pct: 0, started_at: '',
    deadline: '',
  });
  const isNew = !taskId;

  const load = useCallback(async () => {
    setLoading(true);
    if (isNew) {
      setTask(null);
      setForm((f) => ({ ...f, project: projectId || f.project }));
      setLoading(false);
      return;
    }
    try {
      const res = await api.getTask(taskId);
      setTask(res.data);
      setForm({
        title: res.data.title || '',
        description: res.data.description || '',
        assignee: res.data.assignee || '',
        priority: res.data.priority || 'medium',
        due_date: res.data.due_date || '',
        status: res.data.status || 'new',
        project: res.data.project || '',
        is_milestone: !!res.data.is_milestone,
        duration_days: res.data.duration_days || '',
        progress_pct: res.data.progress_pct || 0,
        started_at: res.data.started_at ? res.data.started_at.slice(0, 10) : '',
        deadline: res.data.deadline || '',
      });
      setComments(res.data.comments || []);
      setHistory(res.data.history || []);
      setAttachments(res.data.attachments || []);
      setTimeLogs(res.data.time_logs || []);
      // Загрузим все задачи проекта для списка возможных предшественников
      try {
        const p = res.data.project;
        if (p) {
          const tasksRes = await api.getTasks({ project: p });
          setProjectTasks((tasksRes.data.results || tasksRes.data).filter((x) => x.id !== taskId));
        }
      } catch { /* ignore */ }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [taskId, isNew, projectId]);

  useEffect(() => { load(); }, [load]);

  // Валидация формы
  const validationErrors = (() => {
    const errs = [];
    if (form.started_at && form.due_date) {
      if (new Date(form.due_date) < new Date(form.started_at)) {
        errs.push('Срок (финиш) не может быть раньше даты старта');
      }
    }
    if (form.duration_days && parseInt(form.duration_days, 10) < 0) {
      errs.push('Длительность не может быть отрицательной');
    }
    if (!form.is_milestone && form.duration_days === 0) {
      // милстон = 0 дн., иначе — норма
    }
    if (form.is_milestone && form.duration_days && parseInt(form.duration_days, 10) > 0) {
      errs.push('Веха должна иметь длительность 0 дней');
    }
    if (form.progress_pct && (form.progress_pct < 0 || form.progress_pct > 100)) {
      errs.push('Прогресс должен быть от 0 до 100');
    }
    return errs;
  })();

  const handleSave = async () => {
    if (validationErrors.length > 0) {
      alert('Ошибки в форме:\n\n• ' + validationErrors.join('\n• '));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        assignee: form.assignee || null,
        priority: form.priority,
        due_date: form.due_date || null,
        is_milestone: !!form.is_milestone,
        duration_days: form.duration_days ? parseInt(form.duration_days, 10) : null,
        progress_pct: parseInt(form.progress_pct, 10) || 0,
        deadline: form.deadline || null,
      };
      if (form.started_at) {
        payload.started_at = form.started_at;
      }
      let saved;
      if (isNew) {
        const createPayload = { ...payload, project: form.project, status: 'new' };
        if (parentId) createPayload.parent = parentId;
        saved = await api.createTask(createPayload);
      } else {
        saved = await api.updateTask(taskId, payload);
        if (form.status !== task.status) {
          await api.changeTaskStatusV2(taskId, form.status);
        }
      }
      // Показать пользователю если сдвинулась цепь
      const shifted = saved.data?._shifted_successors;
      const conflicts = saved.data?._deadline_conflicts || [];
      if (Array.isArray(shifted) && shifted.length > 0) {
        let msg = `Задача сохранена. Автоматически перепланировано ${shifted.length} последующих задач `
          + `(сдвинулись даты по цепочке зависимостей).`;
        if (conflicts.length > 0) {
          msg += `\n\n⚠ Внимание! ${conflicts.length} задач(и) теперь нарушают свои дедлайны:\n`;
          for (const c of conflicts.slice(0, 5)) {
            msg += `\n• ${c.title} — опоздание на ${c.days_late} дн.`;
          }
          if (conflicts.length > 5) {
            msg += `\n… и ещё ${conflicts.length - 5}.`;
          }
          msg += '\n\nОткройте эти задачи и решите, что делать.';
        }
        alert(msg);
      }
      onSaved && onSaved(saved.data);
      onClose();
    } catch (e) {
      alert('Не удалось сохранить: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await api.addTaskComment(taskId, newComment);
      setComments((c) => [...c, res.data]);
      setNewComment('');
    } catch (e) {
      alert('Не удалось добавить комментарий');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !taskId) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await api.uploadTaskAttachment(taskId, file, (evt) => {
        if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      setAttachments((a) => [res.data, ...a]);
    } catch (err) {
      alert('Не удалось загрузить файл: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
    setUploadProgress(0);
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('Удалить файл?')) return;
    try {
      await api.deleteTaskAttachment(taskId, attId);
      setAttachments((a) => a.filter((x) => x.id !== attId));
    } catch (err) {
      alert('Не удалось удалить: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddTimeLog = async () => {
    if (!timeForm.hours) return;
    setSavingTime(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.addTaskTimeLog(taskId, {
        hours: timeForm.hours,
        description: timeForm.description,
        logged_date: timeForm.logged_date || today,
      });
      setTimeLogs((l) => [res.data, ...l]);
      setTimeForm({ hours: '', description: '', logged_date: '' });
    } catch (err) {
      alert('Не удалось добавить: ' + (err.response?.data?.detail || err.message));
    }
    setSavingTime(false);
  };

  const handleDeleteTimeLog = async (logId) => {
    if (!window.confirm('Удалить запись?')) return;
    try {
      await api.deleteTaskTimeLog(taskId, logId);
      setTimeLogs((l) => l.filter((x) => x.id !== logId));
    } catch (err) {
      alert('Не удалось удалить: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить задачу?')) return;
    try {
      await api.deleteTask(taskId);
      onSaved && onSaved(null);
      onClose();
    } catch (e) {
      alert('Не удалось удалить: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return (
      <div className="tasks-modal-overlay" onClick={onClose}>
        <div className="tasks-modal" onClick={(e) => e.stopPropagation()}>Загрузка…</div>
      </div>
    );
  }

  return (
    <div className="tasks-modal-overlay" onClick={onClose}>
      <div className="tasks-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        <h2>{isNew ? 'Новая задача' : 'Задача'}</h2>

        {/* Крупные кнопки-действия для исполнителя (только для существующих задач) */}
        {!isNew && task && (
          <div style={{
            display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap',
            padding: 12, background: '#f0f9ff', border: '1px solid #93c5fd',
            borderRadius: 8,
          }}>
            {task.status === 'new' && (
              <button
                className="tasks-btn primary"
                style={{ padding: '10px 18px', fontSize: 14, fontWeight: 600, background: '#059669', borderColor: '#059669' }}
                onClick={async () => {
                  try {
                    await api.changeTaskStatusV2(taskId, 'in_progress');
                    alert('✓ Задача взята в работу. Статус изменён на «В работе».');
                    onSaved && onSaved();
                    onClose();
                  } catch (err) {
                    alert('Не удалось: ' + (err.response?.data?.detail || err.message));
                  }
                }}
              >
                ▶ Начать работу
              </button>
            )}
            {(task.status === 'in_progress' || task.status === 'review') && (
              <>
                <button
                  className="tasks-btn primary"
                  style={{ padding: '10px 18px', fontSize: 14, fontWeight: 600, background: '#16a34a', borderColor: '#16a34a' }}
                  onClick={async () => {
                    if (!window.confirm('Отметить задачу как выполненную?')) return;
                    try {
                      await api.updateTask(taskId, { progress_pct: 100 });
                      await api.changeTaskStatusV2(taskId, 'done');
                      alert('✓ Задача выполнена. Прогресс = 100%, статус «Готово».');
                      onSaved && onSaved();
                      onClose();
                    } catch (err) {
                      alert('Не удалось: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                >
                  ✔ Завершить
                </button>
                {task.status === 'in_progress' && (
                  <button
                    className="tasks-btn"
                    style={{ padding: '10px 18px', fontSize: 14 }}
                    onClick={async () => {
                      try {
                        await api.changeTaskStatusV2(taskId, 'review');
                        alert('Задача передана на проверку начальнику.');
                        onSaved && onSaved();
                        onClose();
                      } catch (err) {
                        alert('Не удалось: ' + (err.response?.data?.detail || err.message));
                      }
                    }}
                  >
                    👀 На проверку
                  </button>
                )}
              </>
            )}
            {task.status === 'done' && (
              <div style={{ padding: 10, background: '#dcfce7', borderRadius: 6, color: '#166534', fontWeight: 600 }}>
                ✓ Задача выполнена {task.completed_at ? fmtDateTime(task.completed_at) : ''}
              </div>
            )}

            <button
              className="tasks-btn"
              style={{ padding: '10px 18px', fontSize: 14, marginLeft: 'auto' }}
              onClick={() => setTab('comments')}
            >
              💬 Написать комментарий
            </button>
            <button
              className="tasks-btn"
              style={{ padding: '10px 18px', fontSize: 14 }}
              onClick={() => setTab('attachments')}
            >
              📎 Приложить файл
            </button>
          </div>
        )}

        {/* Крупная шкала прогресса — прямо в шапке модалки для исполнителя */}
        {!isNew && task && task.status !== 'done' && task.status !== 'cancelled' && (
          <div style={{
            marginBottom: 14, padding: 12,
            background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
          }}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 14 }}>📊 Прогресс выполнения</strong>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#92400e' }}>{form.progress_pct}%</span>
            </div>
            {/* Визуальная полоска прогресса */}
            <div style={{ height: 20, background: '#fef3c7', borderRadius: 10, border: '1px solid #f59e0b', overflow: 'hidden', marginBottom: 8 }}>
              <div style={{
                height: '100%', width: `${form.progress_pct || 0}%`,
                background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                transition: 'width 0.3s',
              }} />
            </div>
            {/* Быстрые кнопки */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[0, 25, 50, 75, 90, 100].map((p) => (
                <button
                  key={p}
                  onClick={async () => {
                    setForm({ ...form, progress_pct: p });
                    try {
                      await api.updateTask(taskId, { progress_pct: p });
                    } catch { /* ignore, will save on Save */ }
                  }}
                  style={{
                    padding: '6px 14px',
                    background: form.progress_pct === p ? '#d97706' : '#fff',
                    color: form.progress_pct === p ? '#fff' : '#92400e',
                    border: '1px solid #d97706',
                    borderRadius: 6, cursor: 'pointer',
                    fontWeight: 600, fontSize: 13,
                  }}
                >
                  {p}%
                </button>
              ))}
            </div>
          </div>
        )}

        {!isNew && (
          <div className="tasks-tabs">
            <div className={`tasks-tab ${tab === 'details' ? 'active' : ''}`} onClick={() => setTab('details')}>
              Подробности
            </div>
            <div className={`tasks-tab ${tab === 'comments' ? 'active' : ''}`} onClick={() => setTab('comments')}>
              Комментарии ({comments.length})
            </div>
            <div className={`tasks-tab ${tab === 'attachments' ? 'active' : ''}`} onClick={() => setTab('attachments')}>
              Файлы ({attachments.length})
            </div>
            <div className={`tasks-tab ${tab === 'time' ? 'active' : ''}`} onClick={() => setTab('time')}>
              ⏱ Время ({timeLogs.length})
            </div>
            <div className={`tasks-tab ${tab === 'deps' ? 'active' : ''}`} onClick={() => setTab('deps')}>
              🔗 Зависимости
            </div>
            <div className={`tasks-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
              История
            </div>
          </div>
        )}

        {(isNew || tab === 'details') && (
          <>
            {isNew && (
              <div className="tasks-form-row">
                <label>Проект*</label>
                <select
                  className="tasks-select"
                  value={form.project}
                  onChange={(e) => setForm({ ...form, project: e.target.value })}
                >
                  <option value="">— выбрать —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="tasks-form-row">
              <label>Название*</label>
              <input
                className="tasks-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div className="tasks-form-row">
              <label>Описание</label>
              <textarea
                className="tasks-textarea"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="tasks-form-row">
              <label>Исполнитель</label>
              <select
                className="tasks-select"
                value={form.assignee}
                onChange={(e) => setForm({ ...form, assignee: e.target.value })}
              >
                <option value="">— не назначен —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.username}{u.position ? ` (${u.position})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="tasks-form-row">
              <label>Приоритет</label>
              <select
                className="tasks-select"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                {TASK_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div className="tasks-form-row">
              <label>Дата старта</label>
              <input
                type="date"
                className="tasks-input"
                value={form.started_at || ''}
                onChange={(e) => setForm({ ...form, started_at: e.target.value })}
              />
            </div>

            <div className="tasks-form-row">
              <label>Длительность (дн.)</label>
              <input
                type="number" min="0"
                className="tasks-input"
                value={form.duration_days || ''}
                onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                placeholder="Календарных дней"
              />
            </div>

            <div className="tasks-form-row">
              <label>Срок (финиш)</label>
              <input
                type="date"
                className="tasks-input"
                value={form.due_date || ''}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>

            <div className="tasks-form-row">
              <label>▽ Дедлайн (контрольная дата)</label>
              <input
                type="date"
                className="tasks-input"
                value={form.deadline || ''}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              />
            </div>

            {form.deadline && form.due_date && (() => {
              const dl = new Date(form.deadline);
              const dd = new Date(form.due_date);
              const diff = Math.round((dl - dd) / (1000 * 60 * 60 * 24));
              if (diff < 0) {
                return (
                  <div style={{
                    margin: '4px 148px 12px',
                    padding: '8px 12px',
                    background: '#fee2e2',
                    border: '1px solid #b91c1c',
                    borderRadius: 6,
                    color: '#7f1d1d',
                    fontSize: 13,
                    fontWeight: 600,
                  }}>
                    ⚠ Нарушение дедлайна: срок ({fmtDate(form.due_date)}) наступает
                    <strong> позже </strong>дедлайна ({fmtDate(form.deadline)}).
                    Опоздание: {-diff} дн.
                  </div>
                );
              }
              if (diff <= 2) {
                return (
                  <div style={{
                    margin: '4px 148px 12px',
                    padding: '8px 12px',
                    background: '#fef3c7',
                    border: '1px solid #d97706',
                    borderRadius: 6,
                    color: '#78350f',
                    fontSize: 13,
                  }}>
                    ⚠ Риск: до дедлайна осталось {diff} дн.
                  </div>
                );
              }
              return null;
            })()}

            <div className="tasks-form-row">
              <label>Веха (◆)</label>
              <input
                type="checkbox"
                checked={!!form.is_milestone}
                onChange={(e) => setForm({ ...form, is_milestone: e.target.checked })}
              />
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>
                Ключевая контрольная точка (утверждение этапа)
              </span>
            </div>

            <div className="tasks-form-row">
              <label>Прогресс, %</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <input
                  type="range" min="0" max="100" step="5"
                  value={form.progress_pct || 0}
                  onChange={(e) => setForm({ ...form, progress_pct: e.target.value })}
                  style={{ flex: 1 }}
                />
                <span style={{ minWidth: 42, textAlign: 'right', fontWeight: 600 }}>
                  {form.progress_pct}%
                </span>
              </div>
            </div>

            {!isNew && (
              <div className="tasks-form-row">
                <label>Статус</label>
                <select
                  className="tasks-select"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {TASK_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div style={{
                margin: '8px 0',
                padding: '10px 14px',
                background: '#fee2e2',
                border: '1px solid #b91c1c',
                borderRadius: 6,
                color: '#7f1d1d',
                fontSize: 13,
              }}>
                <strong>⚠ Ошибки:</strong>
                <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                  {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            <div className="tasks-form-actions">
              {!isNew && (
                <>
                  <button
                    className="tasks-btn"
                    onClick={async () => {
                      const withSubs = window.confirm('Копировать вместе с подзадачами? OK — да, Отмена — только эту задачу.');
                      try {
                        const res = await api.duplicateTask(taskId, withSubs);
                        alert(`Задача скопирована: «${res.data.title}»`);
                        onSaved && onSaved(res.data);
                        onClose();
                      } catch (err) {
                        alert('Не удалось: ' + (err.response?.data?.detail || err.message));
                      }
                    }}
                    title="Создать копию этой задачи"
                  >
                    📋 Дублировать
                  </button>
                  <button className="tasks-btn danger" onClick={handleDelete}>Удалить</button>
                </>
              )}
              <button className="tasks-btn" onClick={onClose}>Отмена</button>
              <button className="tasks-btn primary" onClick={handleSave} disabled={saving || !form.title}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </>
        )}

        {!isNew && tab === 'comments' && (
          <>
            <div className="comment-list">
              {comments.length === 0 && <div className="tasks-empty">Комментариев пока нет</div>}
              {comments.map((c) => (
                <div className="comment-item" key={c.id}>
                  <div>
                    <span className="author">{c.author_detail?.full_name || c.author_detail?.username || 'Пользователь'}</span>
                    <span className="when">{fmtDateTime(c.created_at)}</span>
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{c.text}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <textarea
                className="tasks-textarea"
                placeholder="Написать комментарий…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                style={{ minHeight: 60, flex: 1 }}
              />
              <button className="tasks-btn primary" onClick={handleAddComment} disabled={!newComment.trim()}>
                Отправить
              </button>
            </div>
          </>
        )}

        {!isNew && tab === 'attachments' && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <label className="tasks-btn primary" style={{ cursor: 'pointer' }}>
                📎 Прикрепить файл
                <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} disabled={uploading} />
              </label>
              {uploading && (
                <span style={{ marginLeft: 12, color: '#6b7280', fontSize: 13 }}>
                  Загрузка… {uploadProgress}%
                </span>
              )}
            </div>
            {attachments.length === 0 ? (
              <div className="tasks-empty">Файлов пока нет</div>
            ) : (
              <div>
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 10px', background: '#f9fafb', borderRadius: 6, marginBottom: 6,
                    }}
                  >
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <a
                        href={a.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}
                      >
                        📄 {a.filename}
                      </a>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {(a.size / 1024).toFixed(1)} КБ · {a.uploaded_by_detail?.full_name || a.uploaded_by_detail?.username || '—'} · {fmtDateTime(a.created_at)}
                      </div>
                    </div>
                    <button
                      className="tasks-btn danger"
                      style={{ padding: '4px 10px' }}
                      onClick={() => handleDeleteAttachment(a.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isNew && tab === 'time' && (
          <div>
            {(() => {
              const total = timeLogs.reduce((s, l) => s + parseFloat(l.hours || 0), 0);
              const est = task?.estimated_hours ? parseFloat(task.estimated_hours) : null;
              return (
                <div style={{ marginBottom: 12, padding: 10, background: '#f9fafb', borderRadius: 6 }}>
                  <strong>Всего: {total.toFixed(2)} ч</strong>
                  {est !== null && <span style={{ marginLeft: 8, color: '#6b7280' }}>из {est} ч оценки</span>}
                </div>
              );
            })()}

            <div style={{ display: 'grid', gridTemplateColumns: '100px 130px 1fr auto', gap: 8, marginBottom: 12 }}>
              <input
                className="tasks-input"
                type="number" step="0.25" min="0.25" max="24"
                placeholder="Часы"
                value={timeForm.hours}
                onChange={(e) => setTimeForm({ ...timeForm, hours: e.target.value })}
              />
              <input
                className="tasks-input"
                type="date"
                value={timeForm.logged_date}
                onChange={(e) => setTimeForm({ ...timeForm, logged_date: e.target.value })}
              />
              <input
                className="tasks-input"
                placeholder="Что сделано (необязательно)"
                value={timeForm.description}
                onChange={(e) => setTimeForm({ ...timeForm, description: e.target.value })}
              />
              <button
                className="tasks-btn primary"
                onClick={handleAddTimeLog}
                disabled={savingTime || !timeForm.hours}
              >
                Добавить
              </button>
            </div>

            {timeLogs.length === 0 ? (
              <div className="tasks-empty">Записей пока нет</div>
            ) : (
              <div>
                {timeLogs.map((l) => (
                  <div
                    key={l.id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 10px', background: '#f9fafb', borderRadius: 6, marginBottom: 6,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div>
                        <strong>{parseFloat(l.hours).toFixed(2)} ч</strong>
                        {' — '}
                        {l.user_detail?.full_name || l.user_detail?.username || 'Пользователь'}
                        <span style={{ marginLeft: 8, color: '#9ca3af', fontSize: 12 }}>{fmtDate(l.logged_date)}</span>
                      </div>
                      {l.description && (
                        <div style={{ fontSize: 13, color: '#4b5563', marginTop: 2 }}>{l.description}</div>
                      )}
                    </div>
                    <button
                      className="tasks-btn danger"
                      style={{ padding: '4px 10px' }}
                      onClick={() => handleDeleteTimeLog(l.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isNew && tab === 'deps' && (
          <div>
            <div style={{ marginBottom: 10, color: '#6b7280', fontSize: 13 }}>
              Эта задача может зависеть от другой (предшественника). Формат FS: «предшественник → эта задача».
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <select
                className="tasks-select"
                value={depForm.from_task_id}
                onChange={(e) => setDepForm({ ...depForm, from_task_id: e.target.value })}
                style={{ flex: 1 }}
              >
                <option value="">— выбрать предшественника —</option>
                {projectTasks.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.title}</option>
                ))}
              </select>
              <input
                type="number"
                className="tasks-input"
                style={{ width: 100 }}
                placeholder="Лаг (дн.)"
                value={depForm.lag_days}
                onChange={(e) => setDepForm({ ...depForm, lag_days: e.target.value })}
              />
              <button
                className="tasks-btn primary"
                onClick={async () => {
                  if (!depForm.from_task_id) return;
                  try {
                    await api.addTaskDependency(task.project, {
                      from_task_id: depForm.from_task_id,
                      to_task_id: taskId,
                      type: 'FS',
                      lag_days: parseInt(depForm.lag_days, 10) || 0,
                    });
                    setDepForm({ from_task_id: '', lag_days: 0 });
                    alert('Зависимость добавлена. Обновите страницу проекта.');
                  } catch (err) {
                    alert('Не удалось: ' + (err.response?.data?.detail || err.message));
                  }
                }}
              >
                Добавить
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              📌 Список текущих зависимостей и удаление — через страницу WBS-плана (стрелки на Ганте).
            </div>
          </div>
        )}

        {!isNew && tab === 'history' && (
          <div>
            {history.length === 0 && <div className="tasks-empty">Изменений пока нет</div>}
            {history.map((h) => (
              <div key={h.id} className="history-item">
                <strong>{h.actor_detail?.full_name || h.actor_detail?.username || 'Система'}</strong>
                {' — изменил '}
                <em>{h.field}</em>
                {': '}
                <span style={{ color: '#dc2626' }}>{h.old_value || '∅'}</span>
                {' → '}
                <span style={{ color: '#16a34a' }}>{h.new_value || '∅'}</span>
                <span style={{ marginLeft: 8, color: '#9ca3af' }}>{fmtDateTime(h.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
